import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { Sidebar } from './sidebar.component';

@Component({
  selector: 'ng-sidebar-container',
  template: `
    <div *ngIf="showBackdrop"
      aria-hidden="true"
      class="ng-sidebar__backdrop"
      [ngClass]="backdropClass"
      (click)="_onBackdropClicked()"></div>
    <ng-content select="ng-sidebar"></ng-content>
    <div class="ng-sidebar__content"
      [class.ng-sidebar__content--animate]="animate"
      [ngClass]="contentClass"
      [ngStyle]="_getContentStyle()">
      <ng-content select="[ng-sidebar-content]"></ng-content>
    </div>
  `,
  styles: [`
    :host {
      box-sizing: border-box;
      display: block;
      position: relative;
      height: 100%;
      width: 100%;
      overflow: hidden;
    }

    .ng-sidebar__backdrop {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: #000;
      opacity: 0.75;
      pointer-events: auto;
      z-index: 99999998;
    }

    .ng-sidebar__content {
      overflow: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
    }

    .ng-sidebar__content--animate {
      -webkit-transition: -webkit-transform 0.3s cubic-bezier(0, 0, 0.3, 1), padding 0.3s cubic-bezier(0, 0, 0.3, 1);
      transition: transform 0.3s cubic-bezier(0, 0, 0.3, 1), padding 0.3s cubic-bezier(0, 0, 0.3, 1);
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarContainer implements AfterContentInit, OnChanges, OnDestroy {
  @Input() animate: boolean = true;

  @Input() allowSidebarBackdropControl: boolean = true;
  @Input() showBackdrop: boolean = false;
  @Output() showBackdropChange = new EventEmitter<boolean>();

  @Input() contentClass: string;
  @Input() backdropClass: string;

  private _sidebars: Array<Sidebar> = [];

  constructor(private _ref: ChangeDetectorRef) {
  }

  ngAfterContentInit(): void {
    this._onToggle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showBackdrop']) {
      this.showBackdropChange.emit(changes['showBackdrop'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe();
  }

  _addSidebar(sidebar: Sidebar) {
    this._sidebars.push(sidebar);
    this._subscribe(sidebar);
  }

  _removeSidebar(sidebar: Sidebar) {
    const index = this._sidebars.indexOf(sidebar);
    if (index !== -1) {
      this._sidebars.splice(index, 1);
    }
  }

  _getContentStyle(): CSSStyleDeclaration {
    let left = 0, right = 0, top = 0, bottom = 0;
    let transformStyle: string = null;
    let heightStyle: string = null;
    let widthStyle: string = null;

    for (const sidebar of this._sidebars) {
      if (sidebar._isModeSlide) {
        if (sidebar.opened) {
          const transformDir: string = sidebar._isLeftOrRight ? 'X' : 'Y';
          const transformAmt: string =
            `${sidebar._isLeftOrTop ? '' : '-'}${sidebar._isLeftOrRight ? sidebar._width : sidebar._height}`;
          transformStyle = `translate${transformDir}(${transformAmt}px)`;
        }
      }

      if ((sidebar._isModePush && sidebar.opened) || sidebar.dock) {
        let paddingAmt: number = 0;
        if (sidebar._isModeSlide && sidebar.opened) {
          if (sidebar._isLeftOrRight) {
            widthStyle = '100%';
          } else {
            heightStyle = '100%';
          }
        } else {
          if (sidebar._isDocked || (sidebar._isModeOver && sidebar.dock)) {
            paddingAmt = sidebar._dockedSize;
          } else {
            paddingAmt = sidebar._isLeftOrRight ? sidebar._width : sidebar._height;
          }
        }

        switch (sidebar.position) {
          case 'left':
            left = Math.max(left, paddingAmt);
            break;
          case 'right':
            right = Math.max(right, paddingAmt);
            break;
          case 'top':
            top = Math.max(top, paddingAmt);
            break;
          case 'bottom':
            bottom = Math.max(bottom, paddingAmt);
            break;
        }
      }
    }

    return {
      padding: `${top}px ${right}px ${bottom}px ${left}px`,
      webkitTransform: transformStyle,
      transform: transformStyle,
      height: heightStyle,
      width: widthStyle
    } as CSSStyleDeclaration;
  }

  _onBackdropClicked(): void {
    for (const sidebar of this._sidebars) {
      if (sidebar.opened && sidebar.showBackdrop && sidebar.closeOnClickBackdrop) {
        sidebar.close();
      }
    }
  }

  private _subscribe(sidebar: Sidebar): void {
    sidebar.onOpenStart.subscribe(() => this._onToggle());
    sidebar.onOpened.subscribe(() => this._markForCheck());
    sidebar.onCloseStart.subscribe(() => this._onToggle());
    sidebar.onClosed.subscribe(() => this._markForCheck());
    sidebar.onModeChange.subscribe(() => this._markForCheck());
    sidebar.onPositionChange.subscribe(() => this._markForCheck());
    sidebar._onRerender.subscribe(() => this._markForCheck());
  }

  private _unsubscribe(): void {
    for (const sidebar of this._sidebars) {
      sidebar.onOpenStart.unsubscribe();
      sidebar.onOpened.unsubscribe();
      sidebar.onCloseStart.unsubscribe();
      sidebar.onClosed.unsubscribe();
      sidebar.onModeChange.unsubscribe();
      sidebar.onPositionChange.unsubscribe();

      sidebar._onRerender.unsubscribe();
    }
  }

  private _onToggle(): void {
    if (this._sidebars.length > 0 && this.allowSidebarBackdropControl) {
      const hasOpen = this._sidebars.some(sidebar => sidebar.opened && sidebar.showBackdrop);
      this.showBackdrop = hasOpen;
      this.showBackdropChange.emit(hasOpen);
    }
    setTimeout(() => {
      this._markForCheck();
    });
  }

  private _markForCheck(): void {
    this._ref.markForCheck();
  }
}



// WEBPACK FOOTER //
// ./src/common/sidebar/sidebar-container.component.ts